/* eslint-disable max-len */
import React from 'react'
import Button from '@components/common/Button/Button.component'
import { ProviderData } from '@dataTypes/provider.types'
import { useAppSelector } from '@store/hooks'
import { TIME_OPTIONS } from '../../../../constants/Providers'
import { formatPhoneNumber } from '@utils/numberFormatter.util'

type ReviewSection = {
  id: string
  title: string
  fields?: { label: string; value: string | JSX.Element | undefined }[]
  content?: JSX.Element
}

type ReviewAccordionProps = {
  sections: ReviewSection[]
}

const ReviewAccordion: React.FC<ReviewAccordionProps> = ({ sections }) => {
  return (
    <div className="accordion" id="accordionReview">
      {sections.map((section, idx) => (
        <div className="accordion-item mb-2" key={section.id}>
          <h2 className="ac-header accordion-header">
            <button
              className="accordion-button"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#collapse-${section.id}-${idx}`}
              aria-expanded="true"
              aria-controls={`collapse-${section.id}-${idx}`}
            >
              {section.title}
            </button>
          </h2>
          <div
            id={`collapse-${section.id}-${idx}`}
            className="accordion-collapse collapse show"
          >
            <div className="accordion-body">
              {section.fields
                ? section.fields.map((field, index) => (
                    <div className="row" key={index}>
                      <div className="col col-review-label">{field.label}</div>
                      <div className="col col-review-value text-end">
                        {field.value}
                      </div>
                    </div>
                  ))
                : section.content}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

type ProviderReviewProps = {
  providerData: ProviderData
  onEdit: () => void
}

const formatDateOfBirth = (date: string | undefined): string => {
  if (!date) return ''
  const parsedDate = new Date(date)
  return isNaN(parsedDate.getTime())
    ? date
    : parsedDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
      })
}

// Helper function to format phone using utils (without country code for display)
const formatPhoneForDisplay = (phone: string | undefined): string => {
  if (!phone) return ''
  return formatPhoneNumber(phone, false) // false = no country code
}

const ProviderReview: React.FC<ProviderReviewProps> = ({
  providerData,
  onEdit,
}) => {
  const clinics = useAppSelector((state) => state.clinic.data)
  const operatories = useAppSelector((state) => state.operatory.data)
  const sections = [
    {
      id: 'personal',
      title: 'Personal',
      fields: [
        {
          label: 'Name',
          value:
            `${providerData.firstName} ${providerData.middleName || ''} ${providerData.lastName}`.trim(),
        },
        { label: 'Username', value: providerData.username || 'N/A' },
        {
          label: 'Date of birth',
          value: formatDateOfBirth(providerData.dateOfBirth),
        },
        { label: 'Gender', value: providerData.gender || 'N/A' },
        { label: 'Preferred pronouns', value: providerData.pronouns || 'N/A' },
        { label: 'Marital status', value: providerData.maritalStatus || 'N/A' },
        {
          label: 'Address',
          value:
            `${providerData.address} ${providerData.city || ''} ${providerData.state || ''} ${providerData.zip || ''}`.trim(),
        },
        {
          label: 'Cellphone number',
          value: formatPhoneForDisplay(providerData.mobile),
        },
        { label: 'Email', value: providerData.email },
        {
          label: 'Home number',
          value: providerData.phone
            ? formatPhoneForDisplay(providerData.phone)
            : 'N/A',
        },
        {
          label: 'Work contact',
          value: providerData.work
            ? formatPhoneForDisplay(providerData.work)
            : 'N/A',
        },
      ],
    },
    {
      id: 'work',
      title: 'Work info',
      fields: [
        {
          label: 'State license number',
          value:
            providerData.stateLicenseNumber &&
            providerData.stateWhereLicensed &&
            providerData.expiryDate
              ? `${providerData.stateLicenseNumber} - ${providerData.stateWhereLicensed} (${providerData.expiryDate})`
              : 'N/A',
        },
        { label: 'State Rx ID', value: providerData.stateRxId || 'N/A' },
        { label: 'DEA number', value: providerData.deaNumber || 'N/A' },
        {
          label: providerData.taxIdType === 'SSN' ? 'SSN number' : 'TIN number',
          value:
            providerData.taxIdType === 'SSN'
              ? providerData.socialSecurityNumber || 'N/A'
              : providerData.taxIdNumber || 'N/A',
        },
        {
          label: 'NPI type',
          value:
            providerData.npiType && providerData.npiNumber
              ? `${providerData.npiType} - ${providerData.npiNumber}`
              : 'N/A',
        },
        {
          label: 'Assigned to',
          value: ((): string => {
            const ids =
              providerData.clinicIds && providerData.clinicIds.length > 0
                ? providerData.clinicIds
                : providerData.clinicId
                  ? [providerData.clinicId]
                  : []
            if (ids.length === 0) return 'N/A'
            return ids
              .map(
                (id) =>
                  clinics.find((c) => c.clinic_id === id)?.clinic_name ?? id,
              )
              .join(', ')
          })(),
        },
        {
          label: 'Operatory rooms',
          value:
            operatories.find(
              (operatory) =>
                operatory.operatory_id === providerData.operatoryId,
            )?.name || 'N/A',
        },
        {
          label: 'Staff color',
          value: (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: providerData.providerColor.header,
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  display: 'inline-flex',
                }}
              ></span>
              <span>{providerData.providerColor.label}</span>
            </div>
          ),
        },
        { label: 'Work position', value: providerData.employmentType || 'N/A' },
        {
          label: 'Working hours',
          value:
            providerData.shiftStartTime && providerData.shiftEndTime
              ? `${
                  TIME_OPTIONS.find(
                    (option) => option.value === providerData.shiftStartTime,
                  )?.label
                } - ${
                  TIME_OPTIONS.find(
                    (option) => option.value === providerData.shiftEndTime,
                  )?.label
                }`
              : 'N/A',
        },
      ],
    },
  ]

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pb-1 pt-2">
        <h4 className="provider-review-title">Review the new staff info</h4>
        <Button
          size="sm"
          className="button-tertiary"
          onClick={onEdit}
          leftIcon={<i className="ti ti-pencil"></i>}
        >
          Edit
        </Button>
      </div>
      <ReviewAccordion sections={sections} />
    </>
  )
}

export default ProviderReview
